export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-indigo-600 py-12">
        <div className="mx-auto max-w-2xl px-6">
          <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
          <p className="mt-2 text-indigo-200">Last updated: May 2, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-2xl px-6 py-12">
        <div className="space-y-8 rounded-2xl bg-white p-8 shadow-sm text-gray-600 leading-relaxed">

          <p>
            Lambada is a hyperlocal marketplace that connects local service providers and sellers
            with nearby customers. This policy explains what data we collect and how we use it.
          </p>

          <Section title="What We Collect">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-gray-800">Account info:</strong> Your name, phone number, or email (depending on how you sign in), and your profile photo and service description.</li>
              <li><strong className="text-gray-800">Location:</strong> Your GPS coordinates, stored on our servers to compute accurate distances. Other users only see a coarse area (roughly 1–2 km) — your precise location is never shown to them.</li>
              <li><strong className="text-gray-800">Content:</strong> Messages you send, photos and videos you share in chats, your portfolio gallery (up to 10 images), service tags, and any external reference links (e.g. Instagram) you add to your profile.</li>
            </ul>
          </Section>

          <Section title="Camera & Photos">
            <p>
              We request camera and photo library access only when you choose to upload a profile
              photo, portfolio image, or media in a message. We never access your camera or photos
              in the background.
            </p>
          </Section>

          <Section title="How We Use Your Data">
            <ul className="list-disc pl-5 space-y-2">
              <li>To display your profile to users nearby</li>
              <li>To enable direct messaging between users</li>
              <li>To send push notifications for messages and activity</li>
              <li>To handle safety reports and enforce community guidelines</li>
            </ul>
          </Section>

          <Section title="Data Retention (Privacy-First)">
            <p>We automatically delete data on a rolling basis:</p>
            <ul className="list-disc pl-5 mt-3 space-y-2">
              <li><strong className="text-gray-800">Messages:</strong> Deleted after 30 days</li>
              <li><strong className="text-gray-800">Message media (photos & videos):</strong> Deleted from our servers after 30 days</li>
              <li><strong className="text-gray-800">Conversation threads:</strong> Removed after 90 days with no new messages</li>
              <li><strong className="text-gray-800">Your account & location:</strong> Deleted after 180 days of inactivity</li>
              <li><strong className="text-gray-800">Abuse reports:</strong> Kept for 1 year for safety purposes</li>
              <li><strong className="text-gray-800">Block records:</strong> Kept until you manually unblock the user</li>
            </ul>
          </Section>

          <Section title="Data Sharing">
            <p>
              We do not sell your data. Your public profile (name, photo, bio, tags, and
              availability status) is visible to nearby users. Other users may save your profile
              to their Favourites list; you can see who has favourited you and can block any user
              at any time. We use third-party services only for authentication and infrastructure —
              they process data solely to operate the app.
            </p>
          </Section>

          <Section title="Your Rights">
            <p>You can at any time:</p>
            <ul className="list-disc pl-5 mt-3 space-y-2">
              <li>Edit or delete your profile within the app</li>
              <li>Request a full export or deletion of your data</li>
            </ul>
            <p className="mt-3">
              To request data deletion or export, use the in-app settings or contact us through the app.
            </p>
          </Section>

          <Section title="Children">
            <p>Lambada is not intended for users under 18. We do not knowingly collect data from minors.</p>
          </Section>

          <Section title="Changes">
            <p>
              We may update this policy. Significant changes will be notified via the app.
              Continued use after changes means you accept the updated policy.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions? Email us at{' '}
              <a href="mailto:support@fibosolutions.com" className="text-indigo-600 hover:underline">
                support@fibosolutions.com
              </a>.
            </p>
          </Section>

        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 py-6">
        <p className="text-center text-sm text-gray-400">
          &copy; {new Date().getFullYear()} Lambada. All rights reserved.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold text-gray-900">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
